import React, { useState } from 'react';
import { Calculator, Copy, Trash2, CreditCard, Lightbulb, AlertTriangle, TrendingDown } from 'lucide-react';

const CreditCardPayoffCalculatorTool = () => {
  const [balance, setBalance] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const b = parseFloat(balance);
    const apr = parseFloat(interestRate);
    const payment = parseFloat(monthlyPayment);

    setError('');
    setResult(null);

    if (isNaN(b) || isNaN(apr) || isNaN(payment) || b <= 0 || payment <= 0) {
      setError('Please enter valid positive numbers.');
      return;
    }

    const monthlyRate = apr / 100 / 12;

    // Check if payment is enough to cover interest
    const minPayment = b * monthlyRate;
    if (payment <= minPayment) {
      setError(`Payment must be greater than $${minPayment.toFixed(2)} to pay off the balance. Your payment only covers the interest.`);
      return;
    }

    // Formula: n = -log(1 - (B * r / P)) / log(1 + r)
    // where B = balance, r = monthly rate, P = payment
    let months;
    if (monthlyRate === 0) {
      months = b / payment;
    } else {
      months = -Math.log(1 - (b * monthlyRate / payment)) / Math.log(1 + monthlyRate);
    }

    const totalPaid = months * payment;
    const totalInterest = totalPaid - b;
    const years = Math.floor(months / 12);
    const remainingMonths = Math.ceil(months % 12);

    // Calculate what happens with higher payment
    const higherPayment = payment * 1.5;
    let fasterMonths;
    if (monthlyRate === 0) {
      fasterMonths = b / higherPayment;
    } else {
      fasterMonths = -Math.log(1 - (b * monthlyRate / higherPayment)) / Math.log(1 + monthlyRate);
    }
    const fasterTotalPaid = fasterMonths * higherPayment;
    const fasterTotalInterest = fasterTotalPaid - b;
    const interestSaved = totalInterest - fasterTotalInterest;
    const monthsSaved = months - fasterMonths;

    setResult({
      balance: b,
      apr,
      payment,
      months: Math.ceil(months),
      years,
      remainingMonths,
      totalPaid,
      totalInterest,
      interestPercent: (totalInterest / b) * 100,
      minPayment,
      // Higher payment scenario
      higherPayment,
      fasterMonths: Math.ceil(fasterMonths),
      fasterTotalInterest,
      interestSaved,
      monthsSaved: Math.floor(monthsSaved)
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toString());
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBalance('');
    setInterestRate('');
    setMonthlyPayment('');
    setResult(null);
    setError('');
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">Card Details</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Balance ($)
            </label>
            <input
              type="number"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              placeholder="e.g., 5000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="balance-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (APR %)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 18.99"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Monthly Payment ($)
            </label>
            <input
              type="number"
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(e.target.value)}
              placeholder="e.g., 200"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="monthly-payment-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-payoff-btn"
      >
        <Calculator size={18} />
        Calculate Payoff
      </button>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm flex items-start gap-2" data-testid="error-message">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="payoff-results">
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Time to Pay Off</span>
                  <button
                    onClick={() => copyResult(result.months, 'months')}
                    className={`p-1.5 rounded transition-colors ${
                      copied === 'months' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    data-testid="copy-months"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="text-2xl font-bold text-blue-400" data-testid="months-result">
                  {result.months} months
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {result.years > 0 && `${result.years} year${result.years > 1 ? 's' : ''}`}
                  {result.years > 0 && result.remainingMonths > 0 && ', '}
                  {result.remainingMonths > 0 && `${result.remainingMonths} month${result.remainingMonths > 1 ? 's' : ''}`}
                </div>
              </div>

              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Total Interest Paid</span>
                  <button
                    onClick={() => copyResult(result.totalInterest.toFixed(2), 'interest')}
                    className={`p-1.5 rounded transition-colors ${
                      copied === 'interest' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    data-testid="copy-interest"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="text-2xl font-bold text-yellow-400" data-testid="interest-result">
                  {formatCurrency(result.totalInterest)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {result.interestPercent.toFixed(1)}% of balance
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="border-t border-gray-700 pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Payment Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Current Balance</span>
                  <span className="text-white font-medium">{formatCurrency(result.balance)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Interest Rate (APR)</span>
                  <span className="text-white font-medium">{result.apr}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Monthly Payment</span>
                  <span className="text-white font-medium">{formatCurrency(result.payment)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Minimum Required Payment</span>
                  <span className="text-gray-300 font-medium">{formatCurrency(result.minPayment)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                  <span className="text-gray-400">Total Amount Paid</span>
                  <span className="text-green-400 font-medium">{formatCurrency(result.totalPaid)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pay More, Save More */}
          <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown size={18} className="text-green-400" />
              <h4 className="text-sm font-medium text-green-400">Pay More, Save More</h4>
            </div>
            <p className="text-sm text-gray-300 mb-3">
              If you increased your payment by 50% to <span className="text-green-400 font-medium">{formatCurrency(result.higherPayment)}</span>:
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-2 bg-gray-800 rounded">
                <div className="text-xs text-gray-400">Payoff Time</div>
                <div className="text-lg font-bold text-green-400">{result.fasterMonths} months</div>
              </div>
              <div className="p-2 bg-gray-800 rounded">
                <div className="text-xs text-gray-400">Interest Saved</div>
                <div className="text-lg font-bold text-green-400">{formatCurrency(result.interestSaved)}</div>
              </div>
              <div className="p-2 bg-gray-800 rounded">
                <div className="text-xs text-gray-400">Months Saved</div>
                <div className="text-lg font-bold text-green-400">{result.monthsSaved} months</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips to Reduce Debt */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb size={18} className="text-yellow-400" />
          <h3 className="text-sm font-medium text-gray-300">Tips to Reduce Credit Card Debt</h3>
        </div>
        <div className="space-y-4">
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">1. Pay More Than the Minimum</h4>
            <p className="text-xs text-gray-400">
              Minimum payments mostly cover interest, keeping you in debt longer. Even an extra $50/month can save hundreds in interest and months of payments.
            </p>
          </div>
          
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">2. Use the Avalanche Method</h4>
            <p className="text-xs text-gray-400">
              Pay minimum on all cards, then put extra money toward the highest-interest card first. This mathematically saves the most money.
            </p>
          </div>
          
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">3. Or Try the Snowball Method</h4>
            <p className="text-xs text-gray-400">
              Pay off the smallest balance first for quick wins and motivation, then roll that payment to the next card. Great for building momentum.
            </p>
          </div>
          
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">4. Balance Transfer to 0% APR</h4>
            <p className="text-xs text-gray-400">
              Transfer your balance to a card with 0% introductory APR. Every payment goes to principal. Watch for transfer fees (typically 3-5%).
            </p>
          </div>
          
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">5. Negotiate a Lower Rate</h4>
            <p className="text-xs text-gray-400">
              Call your card issuer and ask for a rate reduction. If you have good payment history, they may lower your APR by several percentage points.
            </p>
          </div>
          
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">6. Stop Adding to the Balance</h4>
            <p className="text-xs text-gray-400">
              Put away the card and use cash or debit. You can't pay off debt while adding more. Create a budget to avoid relying on credit.
            </p>
          </div>
        </div>
      </div>

      {/* APR Reference */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Average Credit Card APRs</h3>
        <div className="grid md:grid-cols-4 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Low Interest</div>
            <div>12% - 16%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Standard</div>
            <div>18% - 24%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Store Cards</div>
            <div>25% - 30%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Bad Credit</div>
            <div>28% - 36%</div>
          </div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-payoff-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CreditCardPayoffCalculatorTool;
